import {
  type PaymentRequest,
  type PaymentRequestTokenEvent,
  type Stripe,
} from '@stripe/stripe-js'
import { makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type Activatable } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { firstValueFrom, fromEventPattern, type Observable } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { fromStream } from '@/utils/mobx-util'
import { fromAbortSignal } from '@/utils/rx-util'
import type { BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'

export interface ApplePayViewModelProps {
  member: WorkspaceMember
  billingProfileStore: BillingProfileStore
  amount$: Observable<number>
  submit: () => Promise<void>
}

export class ApplePayViewModel implements Activatable {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _getLatestAmount

  @observable.ref accessor stripe: Stripe | null = null
  @observable.ref accessor paymentRequest: PaymentRequest | null = null
  @observable accessor canMakePayment = false

  constructor(private readonly _options: ApplePayViewModelProps) {
    makeObservable(this)

    this._getLatestAmount = fromStream(0, _options.amount$)
  }

  /**
   * Prepares the Apple Pay view model by loading the Stripe SDK.
   */
  prepare = task(async (signal?: AbortSignal) => {
    if (!this.stripe) {
      const stripe = await import('@/features/billing/stripe/load-stripe').then(
        (m) => {
          signal?.throwIfAborted()
          return m.getStripe()
        },
      )
      signal?.throwIfAborted()
      runInAction(() => (this.stripe = stripe))
    }

    // Default amount 0.
    const paymentRequest = this.stripe!.paymentRequest({
      requestPayerName: true,
      requestPayerEmail: true,
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Taxfyle',
        amount: 0,
      },
    })
    const canMakePaymentResult = await paymentRequest.canMakePayment()

    signal?.throwIfAborted()
    runInAction(() => {
      this.paymentRequest = paymentRequest
      this.canMakePayment = !!canMakePaymentResult?.applePay
    })
  })

  /**
   * Shows the Apple Pay sheet and waits for user confirmation,
   * then creates the billing method.
   */
  async showApplePay() {
    const signal = this._lifecycle.signal

    const amountToDisplay = this._getLatestAmount()
    const paymentRequest = this.paymentRequest
    if (amountToDisplay <= 0 || !paymentRequest) {
      return
    }

    paymentRequest.update({
      total: {
        label: 'Taxfyle',
        amount: Math.round(amountToDisplay * 100),
      },
    })

    // Event to observable stream - handles unsubscribing from the
    // previous request.
    const event$ = fromEventPattern<PaymentRequestTokenEvent>(
      (handler) => paymentRequest.on('token', handler),
      (handler) => paymentRequest.off('token', handler),
    ).pipe(takeUntil(fromAbortSignal(signal)))
    const event = await firstValueFrom(event$, { defaultValue: null })
    if (!event) {
      return
    }

    // If the amount changed between when we started showing
    // the Apple Pay sheet and the user authorizing,
    // fail the request.
    const latestAmount = this._getLatestAmount()
    if (amountToDisplay !== latestAmount) {
      event.complete('fail')
      return
    }

    await this.handlePaymentMethod(event, signal)
  }

  /**
   * Callback for when Apple Pay returns a payment token.
   * @param event
   * @param signal
   */
  private async handlePaymentMethod(
    event: PaymentRequestTokenEvent,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()

    try {
      const method =
        await this._options.billingProfileStore.createCardBillingMethodForMember(
          this._options.member,
          event.token.id,
        )
      signal?.throwIfAborted()
      await this._options.billingProfileStore.changeDefaultMethod(method)

      // Submitting the job uses the default billing method.
      event.complete('success')
      await this._options.submit()
    } catch (error) {
      event.complete('fail')
      throw error
    }
  }

  activate(): void {
    this._lifecycle.activate()
  }

  deactivate(): void {
    this._lifecycle.deactivate()
    this.prepare.reset()
  }
}
